import { businessStore, userStore } from 'entities/index';
import { createBusiness, WorkingHoursPicker } from 'features/index';
import { IWeekWorkTime } from 'features/working-hours-picker/interfaces/time-table-input.interface';
import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BusinessApi } from 'shared/api';
import { ICreateBusinessDto } from 'shared/api/business/interfaces/create-business-dto.interface';
import { DEFAULT_TIME_TABLE } from 'shared/consts/schedule';
import { Alert, DefaultButton, FormButtonBack, FormInput } from 'shared/ui';
import { normalizePhone } from 'shared/lib';
import { validateForm } from '../model/validate-form';
import styles from './business-registration-form.module.scss';
import { observer } from 'mobx-react';
import { AppLinks } from 'app/router/consts/app-links.enum';

export const BusinessRegistrationForm: React.FC = observer(() => {
  const [validationError, setValidationError] = useState<boolean | string>(
    false
  );
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState(userStore.phone);
  const [weekWorkTime, setWeekWorkTime] =
    useState<IWeekWorkTime>(DEFAULT_TIME_TABLE);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  const handleSubmit = async () => {
    const errors = validateForm({ name, address, phone });
    if (!errors) {
      try {
        const { id: ownerId } = await BusinessApi.getAccountInfo();
        const data: ICreateBusinessDto = {
          name,
          address,
          contactPhone: phone,
          postIndex: null,
          weekWorkTime,
          ownerId,
        };
        let businessId = await createBusiness(data);
        if (!businessId) {
          const accountInfo = await BusinessApi.getAccountInfo();
          businessId = accountInfo.businessId ?? '';
        }
        businessStore.businessId = businessId;
        if (!businessId) {
          setValidationError('Ошибка: не удалось получить ID бизнеса');
          return;
        }
        setAlertMessage('Бизнес успешно создан');
        setTimeout(() => {
          if (isOnboarding) {
            const path =
              AppLinks.CreatePointLink.replace(':id', businessId) +
              '?onboarding=true';
            navigate(path);
          } else {
            userStore.redirectToRoleBasedPage(navigate);
          }
        }, 1500);
      } catch (error) {
        if (error instanceof Error) {
          setValidationError(error.message);
        }
      }
    } else {
      setValidationError(errors);
    }
  };

  return (
    <>
      <div className={styles.form}>
        <div className={styles.form__content}>
          <div className={styles.content__title}>Регистрация бизнеса</div>
          <div className={styles.content__inputs}>
            <div className={styles.inputs__input}>
              <FormInput
                inputTitle="Название организации"
                inputPlaceholder="Название"
                onChange={(value) => {
                  setName(value);
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                inputTitle="Адрес"
                inputPlaceholder="Где находится точка"
                onChange={(value) => {
                  setAddress(value);
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                isPhone={true}
                inputValue={phone}
                inputTitle="Номер телефона для связи"
                onChange={(value) => {
                  const digits = value.match(/\d+/g)?.join('') ?? '';
                  setPhone(normalizePhone(digits));
                }}
              />
            </div>

            <div className={styles.inputs__input}>
              <WorkingHoursPicker
                inputTitle="График работы"
                onChangeTimeTableHandler={(value) => {
                  setWeekWorkTime(value ?? DEFAULT_TIME_TABLE);
                }}
                inputTimeTable={weekWorkTime}
              />
            </div>
          </div>
          <div className={styles.content__buttons}>
            <div className={styles.buttons__error}>{validationError}</div>
            <div
              className={styles.buttons__button_submit}
              onClick={handleSubmit}
            >
              <DefaultButton buttonText="Зарегистрировать" color="white" />
            </div>
            <div className={styles.buttons__button_back}>
              <FormButtonBack />
            </div>
          </div>
        </div>
      </div>
      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}
    </>
  );
});
